'use client';

/**
 * Последние звонки на дашборде.
 *
 * Та же таблица, что и в журнале, — общий `CallsTable`: раньше здесь был
 * свой набор колонок, и один и тот же звонок выглядел на двух экранах
 * по-разному. Понять, какой из них правдивее, было нельзя.
 *
 * Выборка идёт через `GET /api/calls` под фильтрами дашборда. Отдельной
 * ручки «последние» нет намеренно: вторая ручка рано или поздно разошлась
 * бы с первой, и плитки считались бы по одному набору звонков, а строки —
 * по другому.
 *
 * Обновляется само, как и первая страница журнала: свежий звонок должен
 * появиться сверху, за этим на дашборд и смотрят. Признак живого списка
 * виден — иначе строка, появившаяся без действий человека, читается как
 * сбой, а не как новый звонок.
 */

import React from 'react';
import Link from 'next/link';
import { ArrowRight, AlertCircle, Loader2, Search } from 'lucide-react';
import CallsTable from '@/components/calls/CallsTable';
import { useCalls } from '@/lib/api/hooks';
import { ApiError } from '@/lib/api/client';
import type { ReportFilters } from '@/lib/api/types';

export default function RecentCallsTable({ filters }: { filters: ReportFilters }) {
  const calls = useCalls(
    {
      from: filters.from,
      to: filters.to,
      unit: filters.unit,
      source: filters.source,
      employee: filters.employee,
      sortField: 'startedAt',
      sortDir: 'desc',
      page: 1,
      perPage: 10,
    },
    true
  );

  const items = calls.data?.items ?? [];

  /** Ссылка на журнал под теми же фильтрами: «все звонки» должно значить «эти же». */
  const listHref = (() => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `/call-list?${qs}` : '/call-list';
  })();

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-800">Последние звонки</h2>
            {calls.isFetching && <Loader2 size={13} className="animate-spin text-slate-300" />}
            <span
              className="inline-flex items-center gap-1 text-[10px] text-slate-400"
              title="Список обновляется сам раз в полминуты"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              обновляется
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            Десять свежих в выбранном периоде и разрезе
          </p>
        </div>
        <Link href={listHref} className="btn-ghost text-xs">
          Все звонки <ArrowRight size={14} />
        </Link>
      </div>

      <div className="overflow-x-auto">
        <CallsTable
          items={items}
          placeholder={
            calls.isError ? (
              <div className="flex flex-col items-center gap-2">
                <AlertCircle size={28} className="text-red-300" />
                <span className="text-sm text-slate-600">
                  {calls.error instanceof ApiError
                    ? calls.error.message
                    : 'Не удалось загрузить звонки'}
                </span>
                <button onClick={() => void calls.refetch()} className="btn-secondary mt-2">
                  Повторить
                </button>
              </div>
            ) : calls.isPending ? (
              <Loader2 size={24} className="animate-spin text-slate-300 mx-auto" />
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-2">
                <Search size={28} className="text-slate-300" />
                <span className="text-sm text-slate-400">
                  За период звонков не было — под выбранными фильтрами
                </span>
              </div>
            ) : undefined
          }
        />
      </div>
    </div>
  );
}
